import React, { useState } from 'react';
import { Bug, RotateCcw, X } from 'lucide-react';

import { Button } from 'components/ui/button';
import type { RoleplaySessionController } from './useRoleplaySession';
import type { RoleplayVoiceLatency } from './useRoleplayVoiceSession';

const isTerminal = (status?: string) => status === 'completed' || status === 'ended_early';
const formatLatency = (value: number | null | undefined) => (
  value === null || value === undefined ? '—' : `${Math.round(value)} ms`
);

export const RoleplayDebugPanel: React.FC<{
  controller: RoleplaySessionController;
  voiceLatency?: RoleplayVoiceLatency | null;
}> = ({ controller, voiceLatency = null }) => {
  const [open, setOpen] = useState(false);
  const session = controller.session;

  const returnToPrebrief = async () => {
    if (session && !isTerminal(session.status)) {
      const ended = await controller.endPractice('debug_restart');
      if (!ended) return;
    }
    await controller.prepareRetry();
  };

  if (!open) {
    return (
      <button
        type="button"
        aria-label="Open roleplay debug"
        onClick={() => setOpen(true)}
        className="fixed right-0 top-1/2 z-50 flex -translate-y-1/2 flex-col items-center gap-2 rounded-l-xl bg-slate-950 px-2 py-3 text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-white shadow-xl"
        data-testid="roleplay-debug-panel"
      >
        <Bug size={16} />
        <span className="[writing-mode:vertical-rl]">Staff debug</span>
      </button>
    );
  }

  return (
    <aside className="fixed bottom-4 right-4 top-4 z-50 flex w-[min(22rem,calc(100vw-2rem))] flex-col rounded-2xl border border-white/10 bg-slate-950 text-white shadow-2xl" data-testid="roleplay-debug-panel">
      <div className="flex items-start justify-between border-b border-white/10 px-4 py-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Staff debug</p>
          <h2 className="mt-1 text-base font-semibold">Roleplay session</h2>
        </div>
        <button type="button" aria-label="Close roleplay debug" onClick={() => setOpen(false)} className="rounded-lg p-1.5 text-slate-300 hover:bg-white/10 hover:text-white">
          <X size={18} />
        </button>
      </div>

      <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-sm">
          <dt className="text-slate-400">Module run</dt><dd className="text-right font-mono">{controller.flowRunId}</dd>
          <dt className="text-slate-400">Session</dt><dd className="text-right font-mono">{session?.id ?? 'not started'}</dd>
          <dt className="text-slate-400">Status</dt><dd className="text-right font-mono">{session?.status ?? 'prebrief'}</dd>
          <dt className="text-slate-400">Mode</dt><dd className="text-right font-mono">{session?.mode ?? '—'}</dd>
          <dt className="text-slate-400">Expression</dt><dd className="text-right font-mono">{session?.current_expression ?? controller.scenario?.persona.starting_expression ?? '—'}</dd>
          <dt className="text-slate-400">Learner turns</dt><dd className="text-right font-mono">{session?.turn_count ?? 0}</dd>
        </dl>

        <section className="rounded-xl border border-white/10 bg-white/5 p-3" data-testid="roleplay-voice-latency">
          <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Latest Voice turn</h3>
          <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-xs">
            <dt className="text-slate-400">Generation</dt><dd className="text-right font-mono">{voiceLatency?.generation ?? '—'}</dd>
            <dt className="text-slate-400">Provider</dt><dd className="text-right font-mono">{voiceLatency?.voiceProvider ?? '—'}</dd>
            <dt className="text-slate-400">Voice</dt><dd className="text-right font-mono">{voiceLatency?.voice ?? '—'}</dd>
            <dt className="text-slate-400">Transcript final</dt><dd className="text-right font-mono">{formatLatency(voiceLatency?.transcriptionFinalizationMs)}</dd>
            <dt className="text-slate-400">Backend decision</dt><dd className="text-right font-mono">{formatLatency(voiceLatency?.backendDecisionMs)}</dd>
            <dt className="text-slate-400">OpenAI first text</dt><dd className="text-right font-mono">{formatLatency(voiceLatency?.openaiFirstTextMs)}</dd>
            <dt className="text-slate-400">ElevenLabs connect</dt><dd className="text-right font-mono">{formatLatency(voiceLatency?.elevenLabsConnectionMs)}</dd>
            <dt className="text-slate-400">Text → audio</dt><dd className="text-right font-mono">{formatLatency(voiceLatency?.textToFirstAudioMs)}</dd>
            <dt className="text-slate-400">Directive → audio</dt><dd className="text-right font-mono">{formatLatency(voiceLatency?.directiveToFirstAudioMs)}</dd>
            <dt className="text-slate-400">Speech stop → audio</dt><dd className="text-right font-mono">{formatLatency(voiceLatency?.speechStopToFirstAudioMs)}</dd>
            <dt className="text-slate-400">Expression</dt><dd className="text-right font-mono">{formatLatency(voiceLatency?.expressionSelectionMs)}</dd>
            <dt className="text-slate-400">Playback</dt><dd className="text-right font-mono">{formatLatency(voiceLatency?.actorPlaybackMs)}</dd>
            <dt className="text-slate-400">Local queue drain</dt><dd className="text-right font-mono">{formatLatency(voiceLatency?.localPlaybackDrainMs)}</dd>
            <dt className="text-slate-400">Mic handoff</dt><dd className="text-right font-mono">{formatLatency(voiceLatency?.microphoneHandoffMs)}</dd>
          </dl>
        </section>

        <div className="mt-auto rounded-xl border border-amber-300/20 bg-amber-300/10 p-3 text-xs leading-relaxed text-amber-100">
          Returning to the prebrief ends an open attempt. It does not complete course progress or reveal private scenario prompts.
        </div>
        <Button type="button" size="auto" onClick={() => void returnToPrebrief()} disabled={controller.processing} data-testid="roleplay-debug-restart">
          <RotateCcw size={16} /> {controller.processing ? 'Preparing…' : session && !isTerminal(session.status) ? 'End & return to prebrief' : 'Return to prebrief'}
        </Button>
      </div>
    </aside>
  );
};
